import { useNavigate } from 'react-router-dom';
import { Home, User, CreditCard } from 'lucide-react';
import { motion, LayoutGroup, AnimatePresence, useDragControls } from 'framer-motion';
import {
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from 'react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { RouterLink } from 'src/routes/components';

import { customToast } from 'src/utils/custom-toast';

import { useI18n } from 'src/i18n';
import { getAuthFromStorage } from 'src/lib/auth-storage';
import { NotificationBell } from 'src/features/notifications/components/NotificationBell';

import { Iconify } from 'src/components/common/iconify';

import { handleExpiredToken } from 'src/auth/token-manager';

import { NavMobile } from './nav';
import { navData } from '../config-nav-dashboard';
import { MenuButton } from '../components/menu-button';
import { AccountPopover } from '../components/account-popover';
import { LanguagePopover } from '../components/language-popover';

// Animation constants
const ANIMATION_DURATION = 0.85; // seconds
const HEADER_RIGHT_MARGIN = 16;
const HEADER_FALLBACK_WIDTH = 360;

const getDefaultHeaderX = (headerWidth = HEADER_FALLBACK_WIDTH) => {
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;

  return Math.max(HEADER_RIGHT_MARGIN, windowWidth - headerWidth - HEADER_RIGHT_MARGIN);
};

// Notification types
type NotificationType = 'success' | 'error' | 'warning' | 'info';
type NotificationItem = {
  id: string;
  message: string;
  type: NotificationType;
};

// Create a header notification context
type HeaderNotificationContextType = {
  showNotification: (message: string, type: NotificationType) => void;
};

const HeaderNotificationContext = createContext<HeaderNotificationContextType | undefined>(
  undefined
);

export const useHeaderNotification = () => {
  const context = useContext(HeaderNotificationContext);
  if (!context) {
    throw new Error('useHeaderNotification must be used within HeaderNotificationProvider');
  }
  return context;
};

// Helper function to create emoji icons with consistent styling
const createEmojiIcon = (emoji: string) => (
  <span className="flex h-6 w-6 items-center justify-center text-xl leading-none">
    {emoji}
  </span>
);

// Map notification types to icons and colors
const notificationConfig = {
  success: {
    icon: <Iconify icon="eva:checkmark-circle-fill" width={24} />,
    color: '#4caf50',
    gradient: 'linear-gradient(90deg, #43a047, #66bb6a)',
  },
  error: {
    icon: <Iconify icon="eva:alert-triangle-fill" width={24} />,
    color: '#f44336',
    gradient: 'linear-gradient(90deg, #e53935, #ef5350)',
  },
  warning: {
    icon: <Iconify icon="eva:alert-circle-fill" width={24} />,
    color: '#ff9800',
    gradient: 'linear-gradient(90deg, #fb8c00, #ffa726)',
  },
  info: {
    icon: <Iconify icon="eva:info-fill" width={24} />,
    color: '#2196f3',
    gradient: 'linear-gradient(90deg, #1e88e5, #42a5f5)',
  },
};

// Create custom floating header component
export const FloatingHeader = ({
  scrolled,
  leftContent,
  rightContent,
  className,
}: {
  scrolled: boolean;
  leftContent: React.ReactNode;
  rightContent: React.ReactNode;
  className?: string;
}) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;
  const [isMobileState, setIsMobileState] = useState(isMobile);
  const [notification, setNotification] = useState<NotificationItem | null>(null);
  const [isShowingNotification, setIsShowingNotification] = useState(false);
  const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  const [messageWidth, setMessageWidth] = useState(0);

  // Store original position before notification adjustments
  const [originalPosition, setOriginalPosition] = useState<{x: number, y: number} | null>(null);

  // Add state to track header position
  const [position, setPosition] = useState(() => ({
    x: getDefaultHeaderX(),
    y: scrolled ? 12 : 16,
  }));

  // Track mobile state
  useEffect(() => {
    const checkMobile = () => setIsMobileState(window.innerWidth < 640);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isMobileState || isShowingNotification || !headerRef.current) {
      return undefined;
    }

    const syncDefaultPosition = () => {
      const { width } = headerRef.current?.getBoundingClientRect() ?? {
        width: HEADER_FALLBACK_WIDTH,
      };

      setPosition((prev) => ({
        ...prev,
        x: getDefaultHeaderX(width),
      }));
    };

    syncDefaultPosition();

    if (typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(syncDefaultPosition);
    resizeObserver.observe(headerRef.current);

    return () => resizeObserver.disconnect();
  }, [isMobileState, isShowingNotification, leftContent, rightContent]);

  // Update position when scrolled changes and handle window resize
  useEffect(() => {
    if (isMobileState) {
      setPosition({ x: 0, y: 0 });
      return undefined;
    }

    setPosition((prev) => ({
      ...prev,
      y: scrolled ? 12 : 16,
    }));

    // Also handle window resize to maintain right positioning
    const handleResize = () => {
      const { width } = headerRef.current?.getBoundingClientRect() ?? {
        width: HEADER_FALLBACK_WIDTH,
      };
      const newDefaultX = getDefaultHeaderX(width);

      setPosition((prev) => {
        // If currently showing notification, recalculate position for new window size
        if (isShowingNotification && messageWidth > 0) {
          const newMaxX = window.innerWidth - messageWidth - 20;
          const adjustedX = Math.max(20, Math.min(newDefaultX, newMaxX));
          return {
            ...prev,
            x: adjustedX,
          };
        }

        // If not showing notification, update to new default position
        return {
          ...prev,
          x: newDefaultX,
        };
      });

      // Also update original position if it exists
      if (originalPosition && !isShowingNotification) {
        setOriginalPosition((prev) => (prev ? { ...prev, x: newDefaultX } : null));
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobileState, scrolled, isShowingNotification, messageWidth, originalPosition]);

  // Generate a unique ID for notifications
  const generateId = () => `notification-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  // Measure the width of the message and adjust position to prevent overflow
  useEffect(() => {
    if (isMobileState) {
      return undefined;
    }

    if (messageRef.current && isShowingNotification && notification) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        if (messageRef.current) {
          const { width } = messageRef.current.getBoundingClientRect();
          const notificationWidth = width + 120; // Add padding for icon and margins
          setMessageWidth(notificationWidth);
          
          // Store original position if not already stored
          if (!originalPosition) {
            setOriginalPosition({ x: position.x, y: position.y });
          }
          
          // Check if notification would overflow screen and adjust position
          const windowWidth = window.innerWidth;
          const currentX = originalPosition?.x || position.x;
          
          // If notification would go off-screen, adjust position to keep it visible
          if (currentX + notificationWidth > windowWidth - 20) {
            const newX = Math.max(20, windowWidth - notificationWidth - 20);
            setPosition(prev => ({
              ...prev,
              x: newX
            }));
          } else {
            // If notification fits, use original position
            setPosition(prev => ({
              ...prev,
              x: originalPosition?.x || currentX
            }));
          }
        }
      }, 100);
    }
    
    // Reset to original position when notification disappears
    if (!isShowingNotification && originalPosition) {
      setPosition(originalPosition);
      setOriginalPosition(null);
      setMessageWidth(0);
    }
    return undefined;
  }, [
    notification,
    isShowingNotification,
    originalPosition,
    isMobileState,
    position.x,
    position.y,
  ]);

  // Handler for showing notifications - using useCallback to avoid dependency issues
  const showNotification = useCallback((message: string, type: NotificationType) => {
    // Clear any existing timeout
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }

    // Create a new notification
    const newNotification = {
      id: generateId(),
      message,
      type,
    };

    // Set notification state
    setNotification(newNotification);

    // Use a small delay before showing the notification to make the transition more visible
    setTimeout(() => {
      setIsShowingNotification(true);
    }, 50);

    // Set a timeout to hide the notification - longer for longer messages
    const baseTimeout = 4500;
    const messageLength = message.length;
    const dynamicTimeout = Math.max(baseTimeout, messageLength * 60); // 60ms per character, minimum 4.5s
    
    notificationTimeoutRef.current = setTimeout(() => {
      setIsShowingNotification(false);
      // Wait for exit animation before clearing notification
      setTimeout(() => {
        setNotification(null);
      }, ANIMATION_DURATION * 1000);
    }, dynamicTimeout);
  }, []);

  // Override the default toast functions
  useEffect(() => {
    // Store the original methods
    const originalSuccess = customToast.success;
    const originalError = customToast.error;
    const originalWarning = customToast.warning;
    const originalInfo = customToast.info;
    const originalShow = customToast.show;

    // Override methods safely
    customToast.success = (message: string) => {
      showNotification(message, 'success');
      return null;
    };

    customToast.error = (message: string) => {
      showNotification(message, 'error');
      return null;
    };

    customToast.warning = (message: string) => {
      showNotification(message, 'warning');
      return null;
    };

    customToast.info = (message: string) => {
      showNotification(message, 'info');
      return null;
    };

    customToast.show = (message: string) => {
      showNotification(message, 'info'); // Defaulting to 'info' for generic show
      return null;
    };

    // Cleanup function to restore original toast functions
    return () => {
      customToast.success = originalSuccess;
      customToast.error = originalError;
      customToast.warning = originalWarning;
      customToast.info = originalInfo;
      customToast.show = originalShow;
    };
  }, [showNotification]);

  // Cleanup on unmount - simplified arrow function
  useEffect(
    () => () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    },
    []
  );

  // Get notification styling based on type
  const getNotificationStyle = (): React.CSSProperties => {
    if (!notification) return {};

    const typeConfig = notificationConfig[notification.type];

    return {
      backgroundColor: 'hsl(var(--background) / 0.8)',
      backdropFilter: 'blur(20px) saturate(180%)',
      boxShadow: `inset 0 0 0 1px ${typeConfig.color}33, 0 8px 32px rgba(0, 0, 0, 0.12)`,
    };
  };

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ showNotification }), [showNotification]);

  return (
    <HeaderNotificationContext.Provider value={contextValue}>
      <LayoutGroup id="floating-header">
        <motion.div
          initial={false}
          className={className}
          transition={{
            type: 'spring',
            stiffness: 200,
            damping: 30,
            mass: 1,
            duration: ANIMATION_DURATION,
          }}
        >
          <motion.div
            ref={headerRef}
            drag={!isMobileState}
            dragMomentum={false}
            dragElastic={isMobileState ? 0 : 1}
            dragConstraints={!isMobileState ? { top: 0, left: 0, right: 0, bottom: 0 } : undefined}
            dragTransition={
              !isMobileState
                ? { power: 0, timeConstant: 0, modifyTarget: (target: number) => target }
                : undefined
            }
            onDragEnd={
              !isMobileState
                ? (_: any, info: any) => {
                    setPosition((prev) => ({
                      x: prev.x + info.offset.x,
                      y: prev.y + info.offset.y,
                    }));
                  }
                : undefined
            }
            layout
            initial={{ x: isMobileState ? 0 : position.x, y: isMobileState ? 0 : position.y }}
            animate={{
              x: isMobileState ? 0 : position.x,
              y: isMobileState ? 0 : position.y,
              transition: {
                type: 'spring',
                stiffness: isMobileState ? 260 : isShowingNotification ? 400 : 200,
                damping: isMobileState ? 32 : isShowingNotification ? 40 : 30,
                mass: 1,
                duration: ANIMATION_DURATION,
              },
            }}
            className={cn(
              'fixed left-0 top-0',
              isMobileState
                ? 'right-0 flex w-full flex-col gap-1 border-b border-border/5 bg-background/80 px-4 py-1 shadow-sm backdrop-blur-xl backdrop-saturate-[180%]'
                : 'cursor-move items-center justify-between overflow-hidden rounded-full border border-black/[0.06] bg-background/70 px-4 py-2 backdrop-blur-xl backdrop-saturate-[180%] transition-shadow hover:bg-background/80 hover:shadow-xl',
              !isMobileState && (scrolled ? 'shadow-lg' : 'shadow-sm'),
              !isMobileState && 'flex',
            )}
            style={{
              zIndex: isShowingNotification ? 2000 : 1100,
              ...(isShowingNotification ? getNotificationStyle() : {}),
            }}
          >
            <AnimatePresence mode="wait" initial={false}>
              {isShowingNotification && notification ? (
                <motion.div
                  key="notification"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30,
                    mass: 1,
                    duration: ANIMATION_DURATION * 0.7,
                  }}
                  className="flex min-w-[200px] max-w-[90vw] items-center justify-start px-1 py-2"
                >
                  <motion.div
                    layout
                    style={{ color: notificationConfig[notification.type].color, marginRight: 12 }}
                    className="flex items-center justify-center"
                    initial={{ scale: 0.5 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 20, duration: ANIMATION_DURATION * 0.5 }}
                  >
                    {notificationConfig[notification.type].icon}
                  </motion.div>
                  <motion.div
                    ref={messageRef}
                    layout
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 30, delay: 0.1, duration: ANIMATION_DURATION * 0.5 }}
                  >
                    <p className="min-w-[150px] max-w-[200px] truncate text-sm font-medium sm:max-w-[300px] md:max-w-[400px]">
                      {notification.message}
                    </p>
                  </motion.div>
                </motion.div>
              ) : (
                <motion.div
                  key="header-content"
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30, duration: ANIMATION_DURATION * 0.7 }}
                  className="flex w-full items-center justify-between"
                  style={{ gap: isMobileState ? 8 : 0, flexWrap: 'nowrap' }}
                >
                  <motion.div
                    layout
                    className={cn(
                      'flex items-center',
                      isMobileState ? 'flex-1 gap-2' : 'mr-2 gap-1',
                    )}
                  >
                    {leftContent}
                  </motion.div>

                  <motion.div
                    layout
                    className={cn('flex flex-shrink-0 items-center', !isMobileState && 'ml-2')}
                  >
                    {rightContent}
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      </LayoutGroup>
    </HeaderNotificationContext.Provider>
  );
};

// Simple test component for dragging functionality
export const DraggableTest = () => {
  const dragControls = useDragControls();
  const dragHandleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleElement = dragHandleRef.current;
    if (handleElement) {
      handleElement.style.cursor = 'grab';

      const handlePointerDown = (e: PointerEvent) => {
        e.preventDefault();
        dragControls.start(e);
      };

      handleElement.addEventListener('pointerdown', handlePointerDown);

      return () => {
        handleElement.removeEventListener('pointerdown', handlePointerDown);
      };
    }
    return undefined;
  }, [dragControls]);

  return (
    <div className="relative h-[200px] w-full overflow-hidden">
      <motion.div
        drag
        dragControls={dragControls}
        dragListener={false}
        dragMomentum={false}
        style={{
          position: 'absolute',
          top: 50,
          left: 50,
          width: 100,
          height: 100,
          background: 'linear-gradient(45deg, #4263eb 0%, #934dff 100%)',
          borderRadius: 8,
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontWeight: 'bold',
          zIndex: 1000,
        }}
      >
        <div
          ref={dragHandleRef}
          className="flex h-full w-full select-none items-center justify-center"
        >
          DRAG ME
        </div>
      </motion.div>
    </div>
  );
};

export const useHeaderContent = (layoutQuery?: string) => {
  const navigate = useNavigate();
  const [navOpen, setNavOpen] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(true);
  const { t } = useI18n();

  useEffect(() => {
    if (handleExpiredToken()) {
      setIsTokenValid(false);
      navigate('/sign-in', { state: { message: t('header.sessionExpired') } });
    }

    const intervalId = setInterval(() => {
      if (handleExpiredToken()) {
        setIsTokenValid(false);
        navigate('/sign-in', {
          state: { message: t('header.sessionExpired') },
        });
      }
    }, 60000);

    return () => clearInterval(intervalId);
  }, [navigate, t]);

  const auth = getAuthFromStorage();
  const isAuthenticated = !!auth?.token && isTokenValid;
  const user = auth?.user;

  const leftHeaderContent = (
    <>
      <MenuButton
        onClick={() => setNavOpen(true)}
        className="hidden text-foreground sm:inline-flex md:hidden"
      />
      <NavMobile data={navData} open={navOpen} onClose={() => setNavOpen(false)} />

      <RouterLink
        href="/dashboard"
        className="ml-2 flex items-center text-sm font-bold no-underline sm:text-base"
        style={{
          background: 'linear-gradient(90deg, #4263eb, #934dff)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        JJ
      </RouterLink>
    </>
  );

  const authButtons = (
    <div className="flex gap-2">
      <Button variant="ghost" size="sm" asChild className="rounded-full text-xs font-medium hover:bg-primary/10">
        <RouterLink href="/sign-in">{t('header.login')}</RouterLink>
      </Button>
      <Button size="sm" asChild className="rounded-full text-xs font-medium shadow-md" style={{ background: 'linear-gradient(45deg, #4263eb 0%, #934dff 100%)' }}>
        <RouterLink href="/register">{t('header.register')}</RouterLink>
      </Button>
    </div>
  );

  const handleOpenExtension = () => {
    window.postMessage({ type: 'JOBJOURNEY_OPEN_EXTENSION' }, '*');
  };

  const authenticatedContent = (
    <TooltipProvider>
      <div className="flex items-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleOpenExtension}
              className="gap-1 rounded-full px-2 py-1 text-xs font-medium transition-all hover:bg-primary/10 hover:-translate-y-0.5"
            >
              {createEmojiIcon('🔍')}
              <span className="hidden sm:inline">{t('header.jobs')}</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>{t('header.openAssistant')}</TooltipContent>
        </Tooltip>

        <LanguagePopover />

        <NotificationBell />

        <div className="ml-1">
          <AccountPopover
            data={[
              {
                label: t('account.home'),
                href: '/',
                icon: <Home className="h-4 w-4" />,
              },
              {
                label: t('account.profile'),
                href: '/profile',
                icon: <User className="h-4 w-4" />,
              },
              {
                label: t('account.subscription'),
                href: '/subscription',
                icon: <CreditCard className="h-4 w-4" />,
              },
            ]}
            user={{
              displayName: user ? `${user.firstName} ${user.lastName}` : '',
              email: user?.email || '',
              photoURL: user?.profilePictureUrl || '/assets/images/avatar/avatar_25.jpg',
            }}
          />
        </div>
      </div>
    </TooltipProvider>
  );

  return {
    navOpen,
    setNavOpen,
    isAuthenticated,
    leftHeaderContent,
    authButtons,
    authenticatedContent,
  };
};
